import { StructuredDataService } from "@brookbush/structured-data";
import type { LandingPageContent } from "@brookbush/types";

import { useAuth } from "@clerk/nextjs";
import { motion } from "framer-motion";
import {
	BookOpen,
	FileHeartIcon,
	ShieldCheckIcon,
	TicketIcon,
} from "lucide-react";
import type { GetStaticProps } from "next";
import { useRouter } from "next/compat/router";
import { NextSeo } from "next-seo";
import { type PropsWithChildren, useEffect } from "react";

import HomeExplorer from "./components/HomeExplorer";
import HomeNccaAnnouncement from "./components/HomeNccaAnnouncement";
import LandingPageAccreditations from "./info/components/LandingPageAccreditations";
import LandingPageModules from "./info/components/LandingPageModules";
import LandingPageTestimonials from "./info/components/LandingPageTestimonials";
import Faqs from "~/components/Faqs";
import FeatureCards from "~/components/FeatureCards";
import Footer from "~/components/Footer";
import FullWidthCTA from "~/components/FullWidthCTA";
import Hero from "~/components/Hero";
import LandingPagePartners from "~/components/LandingPagePartners";
import LoadingAnimation from "~/components/LoadingAnimation";
import Navbar from "~/components/Navbar";
import { getHomepageFaqs, HOMEPAGE } from "~/lib/homepage";
import type { NextPageWithProps } from "~/types";
import fetcher from "~/utils/fetcher.client";

const features = [
	{
		title: "Acquire an Advanced Credential & CECs/CEUs at the same time!",
		description:
			"Every course you take in pursuit of certification has also been pre-approved for continuing education credits.",
		icon: BookOpen,
		links: [
			{
				href: "/articles/easy-continuing-education-credits-accreditation",
				label: "CEC Approvals",
			},
		],
		thumbnailUrl:
			"https://vczc4mkjnoo13s0s.public.blob.vercel-storage.com/home-why-1.jpeg",
	},
	{
		title: "Best Reviewed Certification and Course Provider",
		description:
			"We are the highest-rated Certification and course provider with 100s of Google Reviews and an average rating of 5 out of 5 stars!",
		icon: ShieldCheckIcon,
		links: [
			{
				href: "https://www.google.com/maps/place/Brookbush+Institute/@40.7470886,-73.9793827,17z/data=!3m1!5s0x89c259042bc3f44d:0xcfda66d9ec348ef6!4m8!3m7!1s0x89c2599677c1a285:0x806ee51940b04b1d!8m2!3d40.7470846!4d-73.9768078!9m1!1b1!16s%2Fg%2F11pck_nqml?entry=ttu",
				label: "Google Reviews",
			},
		],
		thumbnailUrl:
			"https://vczc4mkjnoo13s0s.public.blob.vercel-storage.com/home-why-2.jpeg",
	},
	{
		title: "Monthly Membership, cancel anytime",
		icon: TicketIcon,
		description:
			"The only true monthly membership in the industry, cancel anytime, all courses, features, and study aides included (no additional fees).",
		links: [
			{
				href: "/articles/Cost-of-Personal-Training-Certification",
				label: "Compare Cost",
			},
		],
		thumbnailUrl:
			"https://vczc4mkjnoo13s0s.public.blob.vercel-storage.com/home-why-3.jpeg",
	},
	{
		title: "Better Certifications by Design",
		description:
			"How we're re-inventing certification: an NCCA-accredited CPT Certification exam that stands on its own, and advanced certificates earned through short courses, small exams, and a credit system instead of one high-stakes final.",
		icon: FileHeartIcon,
		links: [
			{
				href: "/info/certified-personal-trainer",
				label: "Certified Personal Trainer (CPT) Programs",
			},
			{
				href: "/articles/better-corrective-exercise-certification",
				label: "Human Movement Specialist",
			},
			{
				href: "/articles/comparing-cost-manual-therapy-certifications",
				label: "Integrated Manual Therapist",
			},
		],
		thumbnailUrl:
			"https://vczc4mkjnoo13s0s.public.blob.vercel-storage.com/home-why-4.png",
	},
];

const HomePage: NextPageWithProps<{
	landingPageContent: LandingPageContent;
	structuredData: string;
}> = ({ landingPageContent, structuredData }) => {
	const { userId } = useAuth();
	const router = useRouter();

	useEffect(() => {
		if (userId) {
			router?.replace("/learning-plan");
		}
	}, [router, userId]);

	const faqs = getHomepageFaqs(landingPageContent.faqs ?? []);

	if (!userId)
		return (
			<>
				<NextSeo
					title={HOMEPAGE.seoTitle}
					titleTemplate="%s"
					description={HOMEPAGE.seoDescription}
					canonical={`${process.env.NEXT_PUBLIC_SITE_URL}`}
					openGraph={{
						title: HOMEPAGE.seoTitle,
						url: `${process.env.NEXT_PUBLIC_SITE_URL}`,
						description: HOMEPAGE.seoDescription,
						images: [
							{
								url: landingPageContent.thumbnail.url,
								width: 800,
								height: 600,
								alt: HOMEPAGE.seoTitle,
							},
						],
					}}
				/>
				<script
					type="application/ld+json"
					id="json-ld"
					dangerouslySetInnerHTML={{
						__html: structuredData,
					}}
				/>

				<main className="-mt-16">
					<Navbar isHomePage />

					<Hero
						imageUrl={landingPageContent.thumbnail.url}
						title={HOMEPAGE.h1}
						announcement={<HomeNccaAnnouncement />}
					/>

					<LandingPagePartners
						partners={landingPageContent.partners}
						customers={landingPageContent.customers}
					/>

					<FeatureCards
						features={features}
						title="Why Choose Brookbush Institute"
					/>

					<HomeExplorer />

					<LandingPageAccreditations
						accreditations={landingPageContent.accreditations}
						showNccaCptEntry
					/>

					<LandingPageModules
						certification={landingPageContent.linkedCertification as any}
					/>

					<LandingPageTestimonials
						testimonials={landingPageContent.testimonials}
					/>

					{faqs.length > 0 && <Faqs faqs={faqs} />}

					<FullWidthCTA footerSentence={HOMEPAGE.footerCtaSentence} />

					<Footer />
				</main>
			</>
		);
	else
		return (
			<motion.div
				className="h-screen w-full"
				key={"loading"}
				initial={{
					opacity: 0,
				}}
				animate={{
					opacity: 1,

					transition: {
						duration: 0.5,
					},
				}}
				exit={{
					opacity: 0,
					transition: {
						duration: 0.2,
					},
				}}
			>
				<LoadingAnimation />
			</motion.div>
		);
};

// eslint-disable-next-line react/display-name
HomePage.Layout = ({ children }: PropsWithChildren<{}>) => <>{children}</>;

export const getStaticProps: GetStaticProps = async () => {
	try {
		const promiseResult = await Promise.all([
			fetcher(`/landing-pages/homepage`),
			StructuredDataService.getOrganization({ include: ["*"] }),
		]);

		for (const result of promiseResult) {
			if (!result) {
				return {
					notFound: true,
				};
			}
		}

		const [landingPageContent, structuredData] = promiseResult;

		return {
			props: {
				landingPageContent,
				structuredData: StructuredDataService.withContext(structuredData),
			},
		};
	} catch (error) {
		console.error("Failed to load homepage data:", error);
		throw error;
	}
};

export default HomePage;
